import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import MainPage from "@/pages/MainPage";
import LobbyView from "@/pages/LobbyView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// User Context - Multi-user session support
const UserContext = createContext(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within UserProvider");
  return context;
};

const UserProvider = ({ children }) => {
  // Current active user
  const [currentUser, setCurrentUser] = useState(null);
  // All users in this session
  const [users, setUsers] = useState([]);

  useEffect(() => {
    // Load users from localStorage
    const savedUsers = JSON.parse(localStorage.getItem("sessionUsers") || "[]");
    const activeUserId = localStorage.getItem("activeUserId");
    
    setUsers(savedUsers);
    
    if (activeUserId && savedUsers.length > 0) {
      const active = savedUsers.find(u => u.id === activeUserId);
      if (active) setCurrentUser(active);
    }
  }, []);

  const saveUsers = (newUsers) => {
    localStorage.setItem("sessionUsers", JSON.stringify(newUsers));
    setUsers(newUsers);
  };

  const addUser = async (name) => {
    try {
      const res = await axios.post(`${API}/users/login`, { username: name });
      const newUser = { id: res.data.id, username: res.data.username };
      
      // Check if user already exists in session
      const exists = users.find(u => u.id === newUser.id);
      if (!exists) {
        const newUsers = [...users, newUser];
        saveUsers(newUsers);
      }
      
      // Set as active
      localStorage.setItem("activeUserId", newUser.id);
      setCurrentUser(newUser);
      
      return newUser;
    } catch (err) {
      throw err;
    }
  };

  const switchUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      localStorage.setItem("activeUserId", userId);
      setCurrentUser(user);
    }
  };

  const removeUser = (userId) => {
    const newUsers = users.filter(u => u.id !== userId);
    saveUsers(newUsers);
    
    // If removing current user, switch to another or clear
    if (currentUser?.id === userId) {
      if (newUsers.length > 0) {
        switchUser(newUsers[0].id);
      } else {
        localStorage.removeItem("activeUserId");
        setCurrentUser(null);
      }
    }
  };

  const logout = () => {
    // Remove current user from session
    if (currentUser) {
      removeUser(currentUser.id);
    }
  };

  const logoutAll = () => {
    localStorage.removeItem("sessionUsers");
    localStorage.removeItem("activeUserId");
    setUsers([]);
    setCurrentUser(null);
  };

  return (
    <UserContext.Provider value={{ 
      currentUser,
      username: currentUser?.username || "",
      userId: currentUser?.id || "",
      users,
      isLoggedIn: !!currentUser,
      addUser,
      switchUser,
      removeUser,
      logout,
      logoutAll
    }}>
      {children}
    </UserContext.Provider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <UserProvider>
        <div className="min-h-screen bg-[#FAF9F7]">
          {/* CRT scanline effect */}
          <div className="crt-overlay" />
          <Routes>
            <Route path="/" element={<MainPage />} />
            <Route path="/lobby/:lobbyId" element={<LobbyView />} />
          </Routes>
          <Toaster 
            position="bottom-right" 
            toastOptions={{
              style: {
                background: '#FFFFFF',
                border: '1px solid rgba(217, 119, 6, 0.3)',
                color: '#1F1F1E',
                fontFamily: 'JetBrains Mono'
              }
            }}
          />
        </div>
      </UserProvider>
    </BrowserRouter>
  );
}

export default App;
